/**
 * The explainer a phone reads on a feature page it cannot run (ROADMAP SEO row,
 * item 2, owner decision A).
 *
 * `/portfolio/risk-assessment` stays indexed — it ranks for "portfolio risk
 * analysis" — but on a phone the tool itself hands over to the desktop, and
 * Google indexes the phone render. A bare hand-over card there reads as a soft
 * 404. So the phone shows the same answer-first copy the page's static shell
 * carries, from the same file (`data/seoFeaturePages.json`), then the hand-over.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import FEATURE_PAGES from '../data/seoFeaturePages.json';
import { matchesPattern } from './phoneRoutes';

interface FeaturePage {
  path: string;
  h1: string;
  h2: string;
  content: {
    direct: string;
    takeaways: string[];
    questions: Array<{ q: string; a: string }>;
  };
}

const PAGES = FEATURE_PAGES as unknown as Record<string, FeaturePage>;

/** The feature page for a pathname (the route and anything below it), or null. */
export function featurePageFor(pathname: string): FeaturePage | null {
  const key = Object.keys(PAGES).find((k) => matchesPattern(`/${k}/*`, pathname));
  return key ? PAGES[key] : null;
}

/** The shell's copy carries a few inline links; the phone card shows their text. */
export function plainText(html: string): string {
  return html
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

const FeatureExplainer: React.FC<{ page: FeaturePage }> = ({ page }) => (
  // Text colour from the theme, explicitly: inheriting it from <body> is how
  // this page went grey in light mode and near-white-on-white when the phone
  // was in dark mode ([[BUGS #430]]).
  <Box component="article" sx={{ px: 2.5, pt: 4, pb: 2, maxWidth: 640, mx: 'auto', color: 'text.primary' }}>
    <Typography component="h1" sx={{ fontWeight: 700, fontSize: '1.6rem', lineHeight: 1.25, mb: 1.5 }}>
      {page.h1}
    </Typography>
    <Typography sx={{ color: 'text.secondary', fontSize: '1.05rem', lineHeight: 1.5, mb: 2.5 }}>
      {page.h2}
    </Typography>
    <Typography sx={{ lineHeight: 1.65, mb: 2.5 }}>{plainText(page.content.direct)}</Typography>

    <Typography component="h2" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 1 }}>
      Key takeaways
    </Typography>
    <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 3 }}>
      {page.content.takeaways.map((t) => (
        <Typography key={t} component="li" sx={{ lineHeight: 1.6, mb: 0.75 }}>
          {plainText(t)}
        </Typography>
      ))}
    </Box>

    <Typography component="h2" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 1.5 }}>
      Common questions
    </Typography>
    {page.content.questions.map((item) => (
      <Box key={item.q} sx={{ mb: 2 }}>
        <Typography component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
          {plainText(item.q)}
        </Typography>
        <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{plainText(item.a)}</Typography>
      </Box>
    ))}
  </Box>
);

export default FeatureExplainer;
